import type { ExposureConfigFromColumn, ExposureEvent } from '@types';
import { EXPOSURE_EVENTS_CONFIG_KEY } from '../model/DeviceConfiguration';
import { normalizeExposureEvent } from './normalizeExposureEvent';

function asConfigObject(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

/**
 * Middleware: traduce config exposure legacy (DB / API) nel contratto attuale.
 * - foodIds / foodId → itemIds
 * - buffet / isBuffet → isMultiItem (e multiItem in blob)
 *
 * Solo lettura: non scrive in DB. Nuove sessioni usano solo i campi nuovi.
 */
export function translateExposureConfig(
  raw: unknown
): ExposureConfigFromColumn {
  const obj = asConfigObject(raw);
  if (!obj) return {};

  const result: ExposureConfigFromColumn = {};

  if (Array.isArray(obj.itemIds)) {
    result.itemIds = obj.itemIds as number[] | number[][];
  } else if (Array.isArray(obj.foodIds)) {
    result.itemIds = obj.foodIds as number[] | number[][];
  }

  if (typeof obj.hasTutorial === 'boolean') {
    result.hasTutorial = obj.hasTutorial;
  }

  if (typeof obj.multiItem === 'boolean') {
    result.isMultiItem = obj.multiItem;
  } else if (typeof obj.isMultiItem === 'boolean') {
    result.isMultiItem = obj.isMultiItem;
  } else if (typeof obj.buffet === 'boolean') {
    result.isMultiItem = obj.buffet;
  } else if (typeof obj.isBuffet === 'boolean') {
    result.isMultiItem = obj.isBuffet;
  }

  if (
    typeof obj.userSpawnPosition === 'string' ||
    typeof obj.userSpawnPosition === 'number'
  ) {
    result.userSpawnPosition = String(obj.userSpawnPosition);
  }

  return result;
}

/**
 * Applica la traduzione sul blob `config` (chiavi column: itemIds, multiItem)
 * e normalizza eventuali eventi embedded `_exposureEvents`.
 * Ritorna una shallow copy; non muta l’input.
 */
export function applyExposureConfigTranslation(
  raw: Record<string, unknown>
): Record<string, unknown> {
  const translated = translateExposureConfig(raw);
  const out: Record<string, unknown> = { ...raw };

  if (translated.itemIds !== undefined) {
    out.itemIds = translated.itemIds;
  }
  delete out.foodIds;
  delete out.foodId;

  if (translated.isMultiItem !== undefined) {
    out.multiItem = translated.isMultiItem;
  }
  delete out.buffet;
  delete out.isBuffet;

  if (translated.hasTutorial !== undefined) {
    out.hasTutorial = translated.hasTutorial;
  }
  if (translated.userSpawnPosition !== undefined) {
    out.userSpawnPosition = translated.userSpawnPosition;
  }

  const embedded = out[EXPOSURE_EVENTS_CONFIG_KEY];
  if (Array.isArray(embedded)) {
    out[EXPOSURE_EVENTS_CONFIG_KEY] = translateExposureEvents(embedded);
  }

  return out;
}

/** Normalizza una lista di eventi exposure (foodShown → itemShown, foodIds → itemIds). */
export function translateExposureEvents(events: unknown[]): ExposureEvent[] {
  const out: ExposureEvent[] = [];
  for (const event of events) {
    const normalized = normalizeExposureEvent(event);
    if (normalized) out.push(normalized);
  }
  return out;
}
